// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// rules.ts — the whole game, as pure functions over a plain state object. No DOM, no network, no
// randomness. Everything else in the game (the renderer, the bot, the netcode, every test) is a
// consumer of this file, and the mechanism audit deliberately re-derives its own reachability
// rather than calling in here, so a bug in this file cannot rubber-stamp itself.
//
// GEOMETRY. The board is an N×N grid of cells. A hedge does NOT live in a cell — it lives on the
// lattice BETWEEN cells, spanning two cell-lengths, which is the whole reason this game is more
// than a maze:
//
//     slot (r,c) for r,c in [0, N-2]  — the "post" at the corner shared by cells
//     (r,c) (r,c+1) (r+1,c) (r+1,c+1)
//
//   a FLAT hedge on post (r,c) severs  (r,c)|(r+1,c)  and  (r,c+1)|(r+1,c+1)
//   an UPRIGHT hedge on post (r,c) severs  (r,c)|(r,c+1)  and  (r+1,c)|(r+1,c+1)
//
// So each post carries at most one hedge (flat and upright would cross), and two collinear hedges
// one post apart would overlap along a cell edge. Both are illegal, and both fall out of the two
// checks in `hedgeLegal` rather than needing a special case.

/** A cell, encoded r*size + c. */
export type Cell = number;

/** A hedge post, encoded (r*(size-1) + c). Orientation is carried separately. */
export type Post = number;

export const FLAT = 0;
export const UPRIGHT = 1;
export type Orient = typeof FLAT | typeof UPRIGHT;

/** A placed hedge, encoded post*2 + orient, so one integer is a whole hedge. */
export type Hedge = number;

export const hedgeOf = (post: Post, o: Orient): Hedge => post * 2 + o;
export const postOf = (h: Hedge): Post => h >> 1;
export const orientOf = (h: Hedge): Orient => (h & 1) as Orient;

/** Which edge of the board a runner is trying to reach. */
export const NORTH = 0;
export const SOUTH = 1;
export const WEST = 2;
export const EAST = 3;
export type Goal = typeof NORTH | typeof SOUTH | typeof WEST | typeof EAST;

export interface Runner {
  /** Where the pawn stands. */
  at: Cell;
  /** The edge this runner must reach. */
  goal: Goal;
  /** Hedges still in hand. */
  hedges: number;
  /**
   * Set when this runner has left the race (their peer dropped). A retired runner is skipped by
   * the turn order and its pawn stays put as an obstacle. Retirement is only ever applied through
   * an explicit ply in the log — never inferred locally — because a branch taken on one peer and
   * not another is precisely what desyncs a lockstep room.
   */
  retired?: boolean;
}

export interface State {
  readonly size: number;
  readonly runners: Runner[];
  /**
   * A stack every runner draws from, or -1 when each runner carries their own supply. Supply lives
   * in exactly ONE of `pool` and `Runner.hedges` — never both — so there is a single place to ask
   * "may this runner plant?" and no way for the two to disagree.
   */
  pool: number;
  /** Whose turn it is, as an index into `runners`. */
  turn: number;
  /** Every hedge on the board, as `hedgeOf` integers. */
  readonly placed: Hedge[];
  /**
   * Severed cell-to-cell links, as a bitmask per cell of blocked directions. Derived from
   * `placed`, kept alongside it because every legality check and every BFS reads it, and
   * recomputing it from the hedge list per node would dominate the search.
   */
  readonly walls: Uint8Array;
  /** Plies played, which is what the ramp and the move cap are counted in. */
  ply: number;
  /** The runner that reached its edge, or -1 while the race is on. */
  winner: number;
}

/** Direction bits, matching the order of `DR`/`DC`. */
export const N_BIT = 1;
export const S_BIT = 2;
export const W_BIT = 4;
export const E_BIT = 8;
const BITS = [N_BIT, S_BIT, W_BIT, E_BIT];
const DR = [-1, 1, 0, 0];
const DC = [0, 0, -1, 1];

export const rowOf = (size: number, cell: Cell): number => (cell / size) | 0;
export const colOf = (size: number, cell: Cell): number => cell % size;
export const cellAt = (size: number, r: number, c: number): Cell => r * size + c;

/** Does this cell sit on the edge the runner is racing to? */
export function atGoal(size: number, goal: Goal, cell: Cell): boolean {
  switch (goal) {
    case NORTH:
      return rowOf(size, cell) === 0;
    case SOUTH:
      return rowOf(size, cell) === size - 1;
    case WEST:
      return colOf(size, cell) === 0;
    default:
      return colOf(size, cell) === size - 1;
  }
}

/** Apply one hedge to a wall mask. Used to build a state and to probe a candidate. */
export function sever(size: number, walls: Uint8Array, h: Hedge): void {
  const span = size - 1;
  const post = postOf(h);
  const r = (post / span) | 0;
  const c = post % span;
  if (orientOf(h) === FLAT) {
    // severs the two vertical links along the post's row
    walls[cellAt(size, r, c)] |= S_BIT;
    walls[cellAt(size, r + 1, c)] |= N_BIT;
    walls[cellAt(size, r, c + 1)] |= S_BIT;
    walls[cellAt(size, r + 1, c + 1)] |= N_BIT;
  } else {
    walls[cellAt(size, r, c)] |= E_BIT;
    walls[cellAt(size, r, c + 1)] |= W_BIT;
    walls[cellAt(size, r + 1, c)] |= E_BIT;
    walls[cellAt(size, r + 1, c + 1)] |= W_BIT;
  }
}

/** Undo one hedge from a wall mask. Only sound when no other hedge severs the same links — which
 *  is exactly what `hedgeLegal`'s overlap rule guarantees, so probe/undo in the search is safe. */
export function unsever(size: number, walls: Uint8Array, h: Hedge): void {
  const span = size - 1;
  const post = postOf(h);
  const r = (post / span) | 0;
  const c = post % span;
  if (orientOf(h) === FLAT) {
    walls[cellAt(size, r, c)] &= ~S_BIT;
    walls[cellAt(size, r + 1, c)] &= ~N_BIT;
    walls[cellAt(size, r, c + 1)] &= ~S_BIT;
    walls[cellAt(size, r + 1, c + 1)] &= ~N_BIT;
  } else {
    walls[cellAt(size, r, c)] &= ~E_BIT;
    walls[cellAt(size, r, c + 1)] &= ~W_BIT;
    walls[cellAt(size, r + 1, c)] &= ~E_BIT;
    walls[cellAt(size, r + 1, c + 1)] &= ~W_BIT;
  }
}

/** Can a pawn step from `cell` in direction `d`? Ignores pawns — see `pawnMoves`. */
export function open(size: number, walls: Uint8Array, cell: Cell, d: number): Cell {
  if ((walls[cell] & BITS[d]) !== 0) return -1;
  const r = rowOf(size, cell) + DR[d];
  const c = colOf(size, cell) + DC[d];
  if (r < 0 || c < 0 || r >= size || c >= size) return -1;
  return cellAt(size, r, c);
}

/**
 * Shortest number of steps from `from` to the runner's goal edge, or -1 if the edge is unreachable.
 * A plain BFS: the board is at most 81 cells, and this is THE hot function — it is both the whole
 * of the "you may not seal someone in" rule and the whole of the bot's evaluation.
 *
 * Pawns are deliberately ignored. A pawn is never an obstacle in the long run (it moves, and it can
 * be jumped), so counting it would make the evaluation jitter for no gain.
 */
export function distance(size: number, walls: Uint8Array, from: Cell, goal: Goal, scratch?: Int16Array): number {
  const n = size * size;
  const seen = scratch && scratch.length >= n ? scratch : new Int16Array(n);
  seen.fill(-1, 0, n);
  const queue = new Int16Array(n);
  let head = 0;
  let tail = 0;
  queue[tail++] = from;
  seen[from] = 0;
  if (atGoal(size, goal, from)) return 0;
  while (head < tail) {
    const cell = queue[head++];
    const d = seen[cell];
    for (let dir = 0; dir < 4; dir++) {
      const next = open(size, walls, cell, dir);
      if (next < 0 || seen[next] !== -1) continue;
      seen[next] = d + 1;
      if (atGoal(size, goal, next)) return d + 1;
      queue[tail++] = next;
    }
  }
  return -1;
}

/** Is `h` a legal hedge on this board, ignoring the no-sealing-in rule? */
export function hedgeFits(state: State, h: Hedge): boolean {
  const span = state.size - 1;
  const post = postOf(h);
  if (post < 0 || post >= span * span) return false;
  const o = orientOf(h);
  const r = (post / span) | 0;
  const c = post % span;

  for (let i = 0; i < state.placed.length; i++) {
    const p = state.placed[i];
    const pp = postOf(p);
    // A post carries at most one hedge — the second would cross the first.
    if (pp === post) return false;
    if (orientOf(p) !== o) continue;
    const pr = (pp / span) | 0;
    const pc = pp % span;
    // Two collinear hedges one post apart would lie on top of each other for a cell-length.
    if (o === FLAT && pr === r && Math.abs(pc - c) === 1) return false;
    if (o === UPRIGHT && pc === c && Math.abs(pr - r) === 1) return false;
  }
  return true;
}

/**
 * The rule that makes this a game rather than a maze: a hedge may lengthen anyone's route, but it
 * may never leave ANY runner with no route at all. Checked by placing it and running one BFS per
 * runner — the honest way, and cheap enough at this board size.
 */
export function hedgeLegal(state: State, h: Hedge, scratch?: Int16Array): boolean {
  if (!hedgeFits(state, h)) return false;
  sever(state.size, state.walls, h);
  let ok = true;
  for (let i = 0; i < state.runners.length && ok; i++) {
    const rn = state.runners[i];
    if (distance(state.size, state.walls, rn.at, rn.goal, scratch) < 0) ok = false;
  }
  unsever(state.size, state.walls, h);
  return ok;
}

/**
 * Where the runner at `who` may step. Standard blocking-game pawn rules: an adjacent rival is
 * jumped straight over; if the cell behind it is walled off or off the board, the jump bends around
 * it instead. Without the bend, two pawns can lock each other against a hedge.
 */
export function pawnMoves(state: State, who: number, out: Cell[] = []): Cell[] {
  out.length = 0;
  const { size, walls } = state;
  const from = state.runners[who].at;
  const occupied = (cell: Cell): boolean => state.runners.some((r, i) => i !== who && r.at === cell);

  for (let d = 0; d < 4; d++) {
    const next = open(size, walls, from, d);
    if (next < 0) continue;
    if (!occupied(next)) {
      out.push(next);
      continue;
    }
    // A rival stands there. Straight over it first.
    const beyond = open(size, walls, next, d);
    if (beyond >= 0 && !occupied(beyond)) {
      out.push(beyond);
      continue;
    }
    // Blocked behind — bend around, perpendicular to the approach.
    const sides = d < 2 ? [2, 3] : [0, 1];
    for (const s of sides) {
      const side = open(size, walls, next, s);
      if (side >= 0 && !occupied(side)) out.push(side);
    }
  }
  return out;
}

export function createState(size: number, runners: Runner[], pool = -1): State {
  return {
    size,
    runners: runners.map((r) => ({ ...r })),
    pool,
    turn: 0,
    placed: [],
    walls: new Uint8Array(size * size),
    ply: 0,
    winner: -1,
  };
}

/** Does supply come from one shared stack rather than from each runner's own? */
export const sharedSupply = (state: State): boolean => state.pool >= 0;

/** How many hedges this runner may still plant — the ONE place that question is answered. */
export function hedgesLeft(state: State, who: number): number {
  return sharedSupply(state) ? state.pool : state.runners[who].hedges;
}

export type Move = { kind: 'step'; to: Cell } | { kind: 'hedge'; hedge: Hedge };

/** Apply a move. Returns false and changes nothing if it was not legal. */
export function play(state: State, move: Move): boolean {
  if (state.winner >= 0) return false;
  const who = state.turn;
  const runner = state.runners[who];

  if (move.kind === 'step') {
    if (!pawnMoves(state, who).includes(move.to)) return false;
    runner.at = move.to;
    if (atGoal(state.size, runner.goal, move.to)) state.winner = who;
  } else {
    if (hedgesLeft(state, who) <= 0) return false;
    if (!hedgeLegal(state, move.hedge)) return false;
    sever(state.size, state.walls, move.hedge);
    state.placed.push(move.hedge);
    if (sharedSupply(state)) state.pool--;
    else runner.hedges--;
  }

  state.ply++;
  if (state.winner < 0) advanceTurn(state);
  return true;
}

/** How many runners are still racing. */
export const activeCount = (state: State): number => state.runners.filter((r) => !r.retired).length;

/** Pass the turn to the next runner still in the race. */
export function advanceTurn(state: State): void {
  const n = state.runners.length;
  for (let i = 1; i <= n; i++) {
    const next = (state.turn + i) % n;
    if (!state.runners[next].retired) {
      state.turn = next;
      return;
    }
  }
}

/**
 * Retire a seat whose player has gone. Deliberately a first-class, logged operation rather than
 * something each peer works out for itself: every peer applies the identical ply at the identical
 * point in the log, so the race stays bit-identical. The last runner standing wins by walkover —
 * a room that collapses to one player must resolve, never sit there.
 */
export function retire(state: State, seat: number): boolean {
  if (seat < 0 || seat >= state.runners.length) return false;
  if (state.runners[seat].retired || state.winner >= 0) return false;
  state.runners[seat].retired = true;
  state.ply++;
  const left = state.runners.findIndex((r) => !r.retired);
  if (activeCount(state) <= 1) {
    state.winner = left;
    return true;
  }
  if (state.turn === seat) advanceTurn(state);
  return true;
}
